import * as React from 'react';
import { useCallback, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { House as HouseIcon } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z as zod } from 'zod';

import type { Unit } from '@/types/v2/unit';
import axios from '@/lib/v2/axios';
import { useUser } from '@/hooks/v2/use-user';
import { UNIT_ROLES, UNIT_ROLES_LABELS, UNIT_ROLES_DESCRIPTIONS } from '@/constants/unit-roles';
import ProfessionalDialog from '@/components/common/professional-dialog';

interface AssignUnitDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userId: string;
}

const schema = zod.object({
  unit_id: zod.string().min(1, 'Please select a unit'),
  role_name: zod.enum([UNIT_ROLES.UNIT_OWNER, UNIT_ROLES.UNIT_TENANT, UNIT_ROLES.UNIT_RESIDENT]),
  move_in_date: zod.string().optional(),
  user_id: zod.string().min(1, 'User ID is required'),
});

type Values = zod.infer<typeof schema>;

export function AssignUnitDialog({ open, onClose, onSuccess, userId }: AssignUnitDialogProps): React.JSX.Element {
  const { community } = useUser();
  const queryClient = useQueryClient();
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [error, setError] = useState<string | null>(null);

  const defaultValues = {
    unit_id: '',
    role_name: UNIT_ROLES.UNIT_RESIDENT,
    move_in_date: '',
    user_id: userId,
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  // Fetch available units for the community
  const { data: fetchedUnits, isLoading: unitsLoading } = useQuery({
    queryKey: ['units', community?.id],
    queryFn: async () => {
      const response = await axios.get('/api/v2/units', {
        params: { community_id: community?.id },
      });
      return response.data;
    },
    enabled: Boolean(community?.id),
  });

  // Normalize units list shape
  const unitsList: Unit[] = React.useMemo(() => {
    if (!fetchedUnits) return [] as Unit[];
    if (Array.isArray(fetchedUnits)) return fetchedUnits as Unit[];
    if (Array.isArray((fetchedUnits).units)) return (fetchedUnits).units as Unit[];
    if (Array.isArray((fetchedUnits).data)) return (fetchedUnits).data as Unit[];
    return [] as Unit[];
  }, [fetchedUnits]);

  const assignUnitMutation = useMutation({
    mutationFn: async (data: Values) => {
      await axios.get('/sanctum/csrf-cookie');
      const response = await axios.post(`/api/v2/units/${data.unit_id}/assign-user`, {
        user_id: data.user_id,
        role_name: data.role_name,
        move_in_date: data.move_in_date || undefined,
      });
      return response.data;
    },
  });

  const handleClose = useCallback(() => {
    onClose();
    reset();
    setError(null);
    setSelectedUnit(null);
  }, [onClose, reset]);

  const handleSuccess = useCallback((data?: any) => {
    onSuccess();
    handleClose();
    queryClient.invalidateQueries({ queryKey: ['residents', userId] });
    
    // Show appropriate message based on whether user was already assigned
    if (data?.already_assigned) {
      toast.success('User is already assigned to this unit');
    } else {
      toast.success('User assigned to unit successfully');
    }
  }, [onSuccess, handleClose, queryClient, userId]);

  const handleAssignUnit = useCallback(
    async (data: Values) => {
      try {
        const result = await assignUnitMutation.mutateAsync(data);
        handleSuccess(result);
      } catch (error: any) {
        console.error(error);
        setError(error?.response?.data?.message || 'An error occurred while assigning the unit');
      }
    },
    [assignUnitMutation, handleSuccess]
  );

  const watchedUnitId = watch('unit_id');

  React.useEffect(() => {
    if (watchedUnitId && unitsList) {
      const unit = unitsList.find((u: Unit) => u.id === watchedUnitId);
      setSelectedUnit(unit || null);
    } else {
      setSelectedUnit(null);
    }
  }, [watchedUnitId, unitsList]);

  return (
    <ProfessionalDialog
      actions={
        <Stack direction="row" spacing={2} sx={{ width: '100%', justifyContent: 'space-between' }}>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <LoadingButton
            disabled={assignUnitMutation.isPending || !watchedUnitId}
            loading={assignUnitMutation.isPending}
            onClick={handleSubmit(handleAssignUnit)}
            variant="contained"
          >
            Assign Unit
          </LoadingButton>
        </Stack>
      }
      icon={<HouseIcon size={24} />}
      maxWidth="md"
      onClose={handleClose}
      open={open}
      subtitle="Select a unit and role to assign to this user"
      title="Assign Unit"
    >
      <form onSubmit={handleSubmit(handleAssignUnit)}>
        <Stack spacing={4}>
          {/* Info Alert */}
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Select a unit to assign to this user.
          </Alert>
          
          {/* Error Alert */}
          {error ? <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert> : null}

          {/* Form Sections */}
          <Stack spacing={4}>
            {/* Unit Selection Section */}
            <Box>
              <Typography sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }} variant="h6">
                Available Units
              </Typography>
              {unitsLoading ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    Loading units...
                  </Typography>
                </Box>
              ) : unitsList && unitsList.length > 0 ? (
                <Controller
                  control={control}
                  name="unit_id"
                  render={({ field }) => (
                    <FormControl error={Boolean(errors.unit_id)} fullWidth>
                      <Autocomplete
                        filterOptions={(options, { inputValue }) => {
                          const searchTerm = inputValue.toLowerCase();
                          return options.filter((option) => {
                            const unitNumber = option.unit_number?.toLowerCase() || '';
                            const address = option.address?.toLowerCase() || '';
                            const city = option.city?.toLowerCase() || '';
                            const state = option.state?.toLowerCase() || '';
                            const zipCode = option.zip_code?.toLowerCase() || '';
                            
                            return (
                              unitNumber.includes(searchTerm) ||
                              address.includes(searchTerm) ||
                              city.includes(searchTerm) ||
                              state.includes(searchTerm) ||
                              zipCode.includes(searchTerm)
                            );
                          });
                        }}
                        getOptionLabel={(option) => 
                          `${option.unit_number ? `Unit #${option.unit_number}` : 'No Unit Number'} - ${option.address}, ${option.city}, ${option.state} ${option.zip_code}`
                        }
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        loading={unitsLoading}
                        noOptionsText="No units found matching your search"
                        onChange={(_, newValue) => {
                          field.onChange(newValue?.id || '');
                        }}
                        options={unitsList}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {unitsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            error={Boolean(errors.unit_id)}
                            helperText={errors.unit_id?.message}
                            label="Search and select a unit"
                            placeholder="Type to search units..."
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Box sx={{ width: '100%' }}>
                              <Typography sx={{ fontWeight: 600, color: 'primary.main' }} variant="body1">
                                {option.unit_number ? `Unit #${option.unit_number}` : 'No Unit Number'}
                              </Typography>
                              <Typography color="text.secondary" variant="body2">
                                {option.address}, {option.city}, {option.state} {option.zip_code}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        sx={{
                          '& .MuiAutocomplete-inputRoot': {
                            py: 1.5,
                          },
                        }}
                        value={unitsList.find(unit => unit.id === field.value) || null}
                      />
                    </FormControl>
                  )}
                />
              ) : (
                <Box sx={{ textAlign: 'center', py: 4, backgroundColor: 'background.default', borderRadius: 2 }}>
                  <Typography color="text.secondary">No units available in this community.</Typography>
                </Box>
              )}
            </Box>

            {/* Role Selection Section */}
            <Box>
              <Typography sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }} variant="h6">
                Unit Role
              </Typography>
              <Controller
                control={control}
                name="role_name"
                render={({ field }) => (
                  <FormControl error={Boolean(errors.role_name)} fullWidth>
                    <InputLabel>Select Unit Role</InputLabel>
                    <Select
                      {...field}
                      disabled={assignUnitMutation.isPending}
                      label="Select Unit Role"
                      sx={{ 
                        '& .MuiSelect-select': { py: 1.5 },
                        '& .MuiMenuItem-root': { py: 1.5 }
                      }}
                    >
                      {Object.entries(UNIT_ROLES).map(([key, value]) => (
                        <MenuItem key={value} value={value}>
                          <Box sx={{ py: 0.5 }}>
                            <Typography sx={{ fontWeight: 600, mb: 0.5 }} variant="body1">
                              {UNIT_ROLES_LABELS[value]}
                            </Typography>
                            <Typography color="text.secondary" variant="body2">
                              {UNIT_ROLES_DESCRIPTIONS[value]}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.role_name ? <FormHelperText sx={{ mt: 1 }}>
                        {errors.role_name.message}
                      </FormHelperText> : null}
                  </FormControl>
                )}
              />
            </Box>

            {/* Move-in Date Section */}
            <Box>
              <Typography sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }} variant="h6">
                Move-in Date
              </Typography>
              <Controller
                control={control}
                name="move_in_date"
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Move-in Date (Optional)</InputLabel>
                    <OutlinedInput 
                      disabled={assignUnitMutation.isPending} 
                      type="date" 
                      {...field}
                      sx={{ py: 1.5 }}
                    />
                    <FormHelperText sx={{ mt: 1 }}>
                      Leave empty to use current date
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Box>

            {/* Selected Unit Details Section */}
            {selectedUnit ? <Box>
                <Typography sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }} variant="h6">
                  Selected Unit Details
                </Typography>
                <Card 
                  sx={{ 
                    backgroundColor: 'background.default',
                    borderRadius: 3,
                    borderColor: 'primary.main',
                    borderWidth: 2
                  }} 
                  variant="outlined"
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          backgroundColor: 'primary.main' 
                        }} />
                        <Typography sx={{ fontWeight: 500 }} variant="body1">
                          <strong>Unit:</strong>{' '}
                          {selectedUnit.unit_number ? `Unit #${selectedUnit.unit_number}` : 'No Unit Number'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          backgroundColor: 'primary.main' 
                        }} />
                        <Typography sx={{ fontWeight: 500 }} variant="body1">
                          <strong>Address:</strong> {selectedUnit.address}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          backgroundColor: 'primary.main' 
                        }} />
                        <Typography sx={{ fontWeight: 500 }} variant="body1">
                          <strong>Location:</strong> {selectedUnit.city}, {selectedUnit.state} {selectedUnit.zip_code}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Box> : null}
          </Stack>
        </Stack>
      </form>
    </ProfessionalDialog>
  );
}
